import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, List, Modal, Row, Spin } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import SaleOrder from "../../../services/api/SaleOrder";
import { paginationDefaults } from "../../../utils/properties";
import { showError, showSuccess, trimData } from "../../../utils/utilities";

const { confirm } = Modal;

const SalesOrderScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    dataList();
  }, []);

  const formikSalesOrder = useFormik({
    initialValues: {
      id: get(state.modal.data, "id", ""),
      position: get(state.modal.data, "position", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.id) {
        errors.id = "Required!";
      }
      if (!values.position) {
        errors.position = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const saveResponse = await SaleOrder.saveSaleOrders(values);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        dataList();
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (page = state.pagination.currentPage) => {
    dispatch(setLoading(true));
    const salesOrder = await SaleOrder.getSaleOrders(`page=${page - 1}&size=${state.pagination.size}`);
    if (!salesOrder.error && salesOrder.data) {
      salesOrder.data.forEach((saleOrder, i) => {
        saleOrder.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(salesOrder, "data", []),
        pagination: {
          currentPage: salesOrder.currentPage ? salesOrder.currentPage + 1 : paginationDefaults.page,
          size: get(salesOrder, "pageSize", paginationDefaults.size),
          totalItems: get(salesOrder, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(salesOrder, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const getSaleOrder = await SaleOrder.getSaleOrdersDetail(id);
    if (!getSaleOrder.error && getSaleOrder.data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data: getSaleOrder.data } }));
    } else {
      showError(get(getSaleOrder, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = async (saleId) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const deleteResponse = await SaleOrder.deleteSaleOrders(saleId);
        if (!deleteResponse.error) {
          showSuccess();
          dataList();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    formikSalesOrder.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <List
        header={<strong>{t("menuLeft.salesOrder")}</strong>}
        bordered
        size="small"
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(page);
          },
        }}
        dataSource={state.tableContent}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Row className="list-actions" key={item.id}>
                <Col key={item.id} onClick={() => editCallback(item.id)}>
                  <EditOutlined className="icon-medium mr-10" />
                </Col>
                {!item.deletable ? (
                  <Col>
                    {" "}
                    <DeleteOutlined className="icon-medium disabled no-pointer" />{" "}
                  </Col>
                ) : (
                  <Col onClick={() => deleteCallback(item.id)}>
                    {" "}
                    <DeleteOutlined className="icon-medium" />
                  </Col>
                )}
              </Row>,
            ]}
          >
            <List.Item.Meta title={<div>{`${item.id} | ${item.position}`}</div>} />
          </List.Item>
        )}
      />

      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.id
            ? state.modal.data.id
            : t("saleOrder.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikSalesOrder.submitForm}
            isDisabled={!(formikSalesOrder.dirty && formikSalesOrder.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="id">
                {t("costCenters.table.id")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="id"
                name="id"
                onChange={formikSalesOrder.handleChange}
                onBlur={formikSalesOrder.handleBlur}
                value={formikSalesOrder.values.id}
                disabled={state.modal.type === "edit" ? true : false}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="position">
                {t("costCenters.table.name")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="position"
                name="position"
                onChange={formikSalesOrder.handleChange}
                onBlur={formikSalesOrder.handleBlur}
                value={formikSalesOrder.values.position}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default SalesOrderScreen;
